import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Briefcase, MapPin, Monitor, Zap, ArrowLeft, CheckCircle2, Clock, DollarSign, Users, AlertCircle, TrendingUp, XCircle, Bookmark, EyeOff } from 'lucide-react';
import { formatPayDisplay } from '../utils/payDisplay';
import UpgradePopup from '../components/UpgradePopup';
import DOMPurify from 'dompurify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobDetail() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [coverLetter, setCoverLetter] = useState('');
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState('');
  const [matchScore, setMatchScore] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('features');

  useEffect(() => {
    axios.get(`${API}/jobs/${jobId}`).then(r => setJob(r.data)).catch(() => navigate('/jobs'));
  }, [jobId, navigate]);

  useEffect(() => {
    if (user && jobId) {
      axios.get(`${API}/jobs/${jobId}/match-score`, { withCredentials: true })
        .then(r => setMatchScore(r.data)).catch(() => {});
      axios.get(`${API}/saved-jobs/${jobId}/check`, { withCredentials: true })
        .then(r => setIsSaved(r.data.saved)).catch(() => {});
    }
  }, [user, jobId]);

  const toggleSave = async () => {
    if (!user) { navigate('/auth'); return; }
    try {
      const { data } = await axios.post(`${API}/saved-jobs/${jobId}`, {}, { withCredentials: true });
      setIsSaved(data.saved);
    } catch (err) { console.error(err); }
  };

  const handleApply = async () => {
    if (!user) { navigate('/auth'); return; }
    setError(''); setApplying(true);
    try {
      await axios.post(`${API}/applications/quick-apply`, { job_id: jobId, cover_letter: coverLetter }, { withCredentials: true });
      setApplied(true);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Failed to apply';
      if (status === 403 && detail.includes('limit')) {
        setUpgradeReason('applications');
        setShowUpgrade(true);
      } else {
        setError(detail);
      }
    }
    setApplying(false);
  };

  if (!job) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"><div className="animate-pulse text-[#1C2A24]/40">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        <Link to="/jobs" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-6 hover:opacity-80"><ArrowLeft size={14} strokeWidth={1.5} /> Back to Jobs</Link>

        <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-6" data-testid="job-detail-card">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[#F4F1E8] flex items-center justify-center shrink-0">
              <Briefcase size={24} className="text-[#047857]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-medium tracking-tight mb-1" style={{fontFamily:'Outfit'}}>{job.title}</h1>
              <p className="text-lg text-[#1C2A24]/70">{job.company}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={toggleSave} className="rounded-full h-10 w-10 p-0 shrink-0" data-testid="save-job-btn">
              <Bookmark size={20} className={isSaved ? "text-[#D97757] fill-[#D97757]" : "text-[#1C2A24]/30"} strokeWidth={1.5} />
            </Button>
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            <span className="tag-pill"><MapPin size={12} className="mr-1" strokeWidth={1.5} />{job.state}</span>
            {(() => {
              const pay = formatPayDisplay(job.salary_range);
              return pay.isDisclosed
                ? <span className="tag-pill" data-testid="job-pay-disclosed"><DollarSign size={12} className="mr-1" strokeWidth={1.5} />{pay.text}</span>
                : <span className="tag-pill text-[#1C2A24]/40" data-testid="job-pay-undisclosed"><EyeOff size={12} className="mr-1" strokeWidth={1.5} />{pay.text}</span>;
            })()}
            <span className="tag-pill"><Clock size={12} className="mr-1" strokeWidth={1.5} />{job.job_type}</span>
            {job.equipment_provided && <span className="tag-pill"><Monitor size={12} className="mr-1" strokeWidth={1.5} />Equipment Provided</span>}
            {job.quick_apply && <span className="tag-pill"><Zap size={12} className="mr-1" strokeWidth={1.5} />Quick Apply</span>}
            <span className="tag-pill"><Users size={12} className="mr-1" strokeWidth={1.5} />{job.applicants || 0} applicants</span>
          </div>

          <div className="mb-8">
            <h2 className="text-lg font-medium mb-3" style={{fontFamily:'Outfit'}}>About This Role</h2>
            <div className="job-description text-[#1C2A24]/70 leading-relaxed"
              data-testid="job-description"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.description || '') }} />
          </div>

          {job.requirements?.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-medium mb-3" style={{fontFamily:'Outfit'}}>Requirements</h2>
              <ul className="space-y-2">
                {job.requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#1C2A24]/70 text-sm"><CheckCircle2 size={14} className="text-[#047857] mt-0.5 shrink-0" strokeWidth={1.5} />{r}</li>
                ))}
              </ul>
            </div>
          )}

          {job.benefits?.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-medium mb-3" style={{fontFamily:'Outfit'}}>Benefits</h2>
              <div className="flex flex-wrap gap-2">
                {job.benefits.map((b, i) => <span key={i} className="tag-pill">{b}</span>)}
              </div>
            </div>
          )}

          {/* Match Score */}
          {user && matchScore && (
            <div className="mb-8 bg-[#A7F3D0]/20 rounded-2xl p-5 border border-[#047857]/10" data-testid="match-score">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-medium flex items-center gap-2" style={{fontFamily:'Outfit'}}>
                  <TrendingUp size={18} className="text-[#047857]" strokeWidth={1.5} /> Your Match Score
                </h2>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center" style={{borderColor: matchScore.score >= 70 ? '#047857' : matchScore.score >= 40 ? '#D97757' : '#E5E0D8'}}>
                    <span className="text-lg font-bold" style={{fontFamily:'Outfit', color: matchScore.score >= 70 ? '#047857' : matchScore.score >= 40 ? '#D97757' : '#1C2A24'}}>{matchScore.score}%</span>
                  </div>
                </div>
              </div>
              {matchScore.score === 0 && matchScore.message && (
                <p className="text-sm text-[#D97757]">{matchScore.message}</p>
              )}
              {matchScore.matched_skills.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs text-[#1C2A24]/50 mb-1">Matching Skills:</p>
                  <div className="flex flex-wrap gap-1">
                    {matchScore.matched_skills.map((s, i) => <span key={i} className="inline-flex items-center gap-1 text-xs bg-[#047857]/10 text-[#047857] px-2 py-0.5 rounded-full"><CheckCircle2 size={10} />{s}</span>)}
                  </div>
                </div>
              )}
              {matchScore.missing_skills?.length > 0 && (
                <div>
                  <p className="text-xs text-[#1C2A24]/50 mb-1">Skills to develop:</p>
                  <div className="flex flex-wrap gap-1">
                    {matchScore.missing_skills.map((s, i) => <span key={i} className="inline-flex items-center gap-1 text-xs bg-[#F4F1E8] text-[#1C2A24]/50 px-2 py-0.5 rounded-full"><XCircle size={10} />{s}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Apply Section */}
        <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="apply-section">
          {applied ? (
            <div className="text-center py-8">
              <CheckCircle2 size={48} className="text-[#047857] mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="text-xl font-medium mb-2" style={{fontFamily:'Outfit'}}>Application Submitted!</h2>
              <p className="text-[#1C2A24]/60 mb-4">Your profile and resume have been sent to the employer.</p>
              <Link to="/applications"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="track-application-btn">Track Application</Button></Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-medium mb-4" style={{fontFamily:'Outfit'}}>
                {job.quick_apply ? 'Quick Apply' : 'Apply Now'}
              </h2>
              {job.quick_apply && (
                <p className="text-sm text-[#1C2A24]/60 mb-4">Your profile information (name, email, phone, skills, resume) will be automatically included with your application.</p>
              )}
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-2xl text-sm mb-4 flex items-center gap-2" data-testid="apply-error">
                  <AlertCircle size={16} strokeWidth={1.5} />{error}
                </div>
              )}
              <div className="mb-4">
                <label className="text-sm text-[#1C2A24]/70 mb-2 block">Cover Letter (optional)</label>
                <Textarea data-testid="cover-letter-input" value={coverLetter} onChange={e => setCoverLetter(e.target.value)}
                  placeholder="Write a brief cover letter to stand out..." rows={4}
                  className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8]" />
              </div>
              <Button data-testid="apply-button" onClick={handleApply} disabled={applying}
                className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12 px-8">
                {applying ? 'Submitting...' : job.quick_apply ? 'Quick Apply' : 'Submit Application'}
              </Button>
            </>
          )}
        </div>
      </div>

      {showUpgrade && (
        <UpgradePopup
          reason={upgradeReason}
          onClose={() => {
            setShowUpgrade(false);
            sessionStorage.setItem('wfj-upgrade-dismissed', 'true');
          }}
        />
      )}
    </div>
  );
}
